import React from "react"
import {
  TextArea,
  Input,
  FileInput,
  Checkbox,
  FormField,
  SelectEntries,
  Error,
} from "@bw/forms"
import axios from "axios"
import { Field } from "formik"
import * as yup from "yup"
import parse from "html-react-parser"
import { Select } from "../../forms/select/Select"

/**
 * Filters the fields visibility
 */
export const getVisibleFields = (fields, formik) => {
  return fields.filter(field => {
    if (!field.if && !field.if_any) {
      return true
    }

    /* Matching ANY of the conditions will show the field */
    if (field.if_any) {
      for (const target in field.if_any) {
        const parts = field.if_any[target].split(" ")
        const condition = parts.shift()
        const match = parts.join(" ")

        if (conditionIsMet(match, condition, formik.values[target])) {
          return true
        }
      }
    }

    /* Matching ALL of the conditions will show the field */
    if (field.if) {
      for (const target in field.if) {
        const parts = field.if[target].split(" ")
        const condition = parts.shift()
        const match = parts.join(" ")

        if (!conditionIsMet(match, condition, formik.values[target])) {
          return false
        }
      }

      return true
    }

    return false
  })
}

const conditionIsMet = (match, condition, value) => {
  switch (condition) {
    case "equals":
      return match === value

    default:
      return false
  }
}

export const displayField = (field, formik) => {
  const required = field?.validate?.join(" ")?.includes("required")
  const title = field.display ? parse(field.display) : undefined

  switch (field.type) {
    case "textarea":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            placeholder={field.placeholder}
            name={field.handle}
            component={TextArea}
          />
        </FormField>
      )

    case "checkboxes":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          {Object.values(field.options).map((label, i) => {
            const value = Object.keys(field.options)[i]
            return (
              <label
                key={value}
                style={{ display: "flex", gap: "5px", alignItems: "center" }}
              >
                <Field component={Checkbox} name={field.handle} value={value} />
                {label || value}
              </label>
            )
          })}
        </FormField>
      )

    case "toggle":
      return (
        <FormField
          name={field.handle}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <label style={{ display: "flex", gap: "5px", alignItems: "start" }}>
            <Field
              component={Checkbox}
              name={field.handle}
              value={field.value}
              style={{ marginTop: "1px" }}
            />
            <span>{field?.display ? parse(field.display) : null}</span>
          </label>
        </FormField>
      )

    case "radio":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          {Object.values(field.options).map((label, i) => {
            const value = Object.keys(field.options)[i]
            return (
              <label
                key={value}
                style={{ display: "flex", gap: "5px", alignItems: "center" }}
              >
                <Field type="radio" name={field.handle} value={value} />
                {label || value}
              </label>
            )
          })}
        </FormField>
      )

    case "select":
      const options = Object.entries(field.options).map(([value, label]) => ({
        value,
        label,
      }))

      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field component={Select} name={field.handle} options={options} />
        </FormField>
      )

    case "entries":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            component={SelectEntries}
            name={field.handle}
            collections={field.collections}
            isMulti={true}
            isOptionDisabled={() => {
              return formik.values[field.handle].length >= field.max_items
            }}
          />
        </FormField>
      )

    case "assets":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            name="file"
            onChange={event => {
              formik.setFieldValue(field.handle, [...event.currentTarget.files])
            }}
            component={FileInput}
          />
        </FormField>
      )

    case "integer":
      return (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            type="number"
            step="1"
            placeholder={field.placeholder}
            name={field.handle}
            component={Input}
          />
        </FormField>
      )

    case "text":
    case "number":
    default:
      if (field.handle === "page_title") {
        field.input_type = "hidden"
      }
      return field.input_type === "hidden" ? (
        <Field
          type={field.input_type}
          name={field.handle}
          component={Input}
          value={resolvePlaceholder(field.placeholder)}
        />
      ) : (
        <FormField
          name={field.handle}
          title={title}
          help={field.instructions}
          helpPosition={field.instructions_position}
          required={required}
        >
          <Field
            type={field.input_type}
            placeholder={field.placeholder}
            name={field.handle}
            component={Input}
          />
        </FormField>
      )
  }
}

const resolvePlaceholder = placeholder => {
  switch (placeholder) {
    case "title":
      return document.title
    default:
      return undefined
  }
}

export const getFormFields = handle => {
  return axios({
    method: "GET",
    url: `/api/forms/${handle}`,
  }).then(response => {
    return response.data.data.fields
  })
}

/**
 * post form data
 */
export const postFormFields = (handle, values) => {
  const dataToSend = new FormData()
  dataToSend.append("_method", "POST")
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === "object" && value.length > 0) {
      // these are files
      value.forEach(file => {
        dataToSend.append(`${key}[]`, file)
      })
    } else {
      dataToSend.append(key, value)
    }
  })

  return axios({
    method: "POST",
    url: `/!/forms/${handle}`,
    data: dataToSend,
    headers: {
      "Content-Type": "multipart/form-data",
      "X-Requested-With": "XMLHttpRequest",
    },
  }).then(response => {
    if (response.status !== 200) {
      throw new Error("Invalid server response")
    }
    return response
  })
}

const validateIf = (values, conditions) => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    const parts = conditions[i].split(" ")
    const condition = parts.shift()
    const match = parts.join(" ")

    if (!conditionIsMet(match, condition, value)) {
      return false
    }
  }

  return true
}

const validateIfAny = (values, conditions) => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    const parts = conditions[i].split(" ")
    const condition = parts.shift()
    const match = parts.join(" ")

    if (conditionIsMet(match, condition, value)) {
      return true
    }
  }

  return false
}

const getRequiredFieldValidator = field => {
  let dependencies
  let validator

  if (field.if) {
    dependencies = Object.keys(field.if)
    validator = values => validateIf(values, Object.values(field.if))
  }

  if (field.if_any) {
    dependencies = Object.keys(field.if_any)
    validator = values => validateIfAny(values, Object.values(field.ifAny))
  }

  return [dependencies, validator]
}

export const getInitialValues = fields => {
  return Object.values(fields).reduce((acc, field) => {
    switch (field.type) {
      case "toggle":
        acc[field.handle] = false
        break

      case "entries":
        acc[field.handle] = []
        break

      case "checkboxes":
        acc[field.handle] = []
        break

      case "select":
      case "radio":
        acc[field.handle] = Object.keys(field.options)[0]
        break

      case "text":
      case "textarea":
      default:
        acc[field.handle] = field.default || ""

        if (field.handle === "page_title") {
          acc[field.handle] = document.querySelector("h1").textContent
        }

        break
    }
    return acc
  }, {})
}

const fieldType = type => {
  switch (type) {
    case "text":
    case "textarea":
    case "radio":
    case "select":
      return "string"

    case "toggle":
      return "bool"

    case "number":
    case "integer":
      return "number"

    case "email":
      return "email"

    case "checkboxes":
      return "array"

    case "assets":
    default:
      return "mixed"
  }
}

export const fieldsSchema = (fields, t) => {
  return Object.values(fields).reduce((acc, field) => {
    // gets yup.string(), yup.number(), etc.
    const yupType = fieldType(field.type)
    let yupField = yup[yupType]()

    if (field.validate && field.validate.join(" ").includes("required")) {
      /**
       * Field is required, without conditional relation
       */
      if (!field.if && !field.if_any) {
        switch (yupType) {
          case "bool":
            yupField = yupField.oneOf([true], t("form.required"))
            break

          case "array":
            yupField = yupField.min(1, t("form.required")).of(yup.string())
            break

          default:
            yupField = yupField.required()
        }
      } else {
        /**
         * Field is required, with conditional relation
         */
        const [dependencies, validator] = getRequiredFieldValidator(field)
        switch (yupType) {
          case "bool":
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema => schema.oneOf([true], t("form.required")),
            })
            break

          case "array":
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema =>
                schema.min(1, t("form.required")).of(yup.string()),
            })
            break

          default:
            yupField = yupField.when(dependencies, {
              is: (...params) => validator(params),
              then: schema => schema.required(),
            })
        }
      }
    } else {
      /**
       * Field is NOT required
       */
      yupField = yupField.nullable()
    }

    if (field.type === "integer") {
      yupField = yupField.integer(t("form.mustBeInteger"))
    }

    acc[field.handle] = yupField
    return acc
  }, {})
}
